import React, { useState, useEffect, useRef } from 'react';
import { Copy, Code, FileText, Info, MailQuestion, Download, FilePlus2, Bookmark, CalendarClock, Share2, CheckCheck, MoreHorizontal } from 'lucide-react';
import Button from '../Button';
import PopupCard from '../PopupCard';
import SyntaxHighlighter from 'react-syntax-highlighter';
import { docco } from 'react-syntax-highlighter/dist/esm/styles/hljs';
import { useAuth } from '../../contexts/AuthContext';
import { getFirestore, doc, getDoc, updateDoc, collection, query, where, getDocs, limit } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase';
import { format } from 'date-fns';
import ShareQueryResult from './ShareQueryResult';
import RatingButton from './RatingButton';
import * as XLSX from 'xlsx';
import { createImageWrapper, captureImage } from './imageUtils';
import { useConfig } from '../../contexts/ConfigContext';
import DownloadAllButton from './DownloadAllButton'; 
import LogsModal from './LogsModal';
import ScheduleQueryModal from './ScheduleQueryModal';


const ActionPanel = ({ 
  result, 
  onSaveQuery, 
  onAddToLibrary,
  isSavedQuery, 
  onExplanationClick, 
  isExplanationLoading, 
  isExplanationPending,
  explanation, 
  isDashboardMode, 
  isEmptyResult,
  isInDashboard,
  downloadExcel,
  viewMode,
  chartRef,
  cardTitle,
  statsData,
  selectedYAxis,
  truncationMessage,
  isMobile = false  // ADD THIS PROP
}) => {
  const [supportEmails, setSupportEmails] = useState([]);
  const [showSqlPopup, setShowSqlPopup] = useState(false);
  const [showLogsPopup, setShowLogsPopup] = useState(false);
  const [showLogicPopup, setShowLogicPopup] = useState(false);
  const [copiedResult, setCopiedResult] = useState(false);
  const [copiedSql, setCopiedSql] = useState(false);
  const [addingToLibrary, setAddingToLibrary] = useState(false);
  const { isAdmin, currentUser } = useAuth(); 
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const { config } = useConfig();
  const [copiedShareUrl, setCopiedShareUrl] = useState(false);
  const [isGeneratingShareUrl, setIsGeneratingShareUrl] = useState(false);
  const [copiedLinkInModal, setCopiedLinkInModal] = useState(false);
  const [showSharePrivateWarning, setShowSharePrivateWarning] = useState(false);
  const [showShareConfirmation, setShowShareConfirmation] = useState(false);
  const [isQuerySaved, setIsQuerySaved] = useState(false); // Start with false by default
  const manualSaveRef = useRef(false);
  const lastCheckedDocId = useRef(null);
  const [savedId, setSavedId] = useState(null);
  const db = getFirestore();
  const [scheduledQueryData, setScheduledQueryData] = useState(null);
  const [isLoadingSchedule, setIsLoadingSchedule] = useState(false);

  // ADD MOBILE DROPDOWN STATE
  const [showMobileDropdown, setShowMobileDropdown] = useState(false);
  const mobileDropdownRef = useRef(null);

  const checkIfQueryIsSaved = async (documentId) => {
    if (!documentId || !currentUser?.email) return;
    
    try {
      const savedQueryRef = collection(db, 'SavedQueries');
      const q = query(
        savedQueryRef,
        where('originalLlmQueryId', '==', documentId),
        where('userId', '==', currentUser.email),
        where('isDeleted', '==', false)
      );
      
      const querySnapshot = await getDocs(q);
      const isSaved = !querySnapshot.empty;
      
      // Only update if not manually saved already
      if (!manualSaveRef.current) {
        setIsQuerySaved(isSaved);
        
        // If it's saved, update the savedId for potential later use
        if (isSaved && querySnapshot.docs[0]) {
          const savedQueryId = querySnapshot.docs[0].id;
          setSavedId(savedQueryId);
          // Don't fetch scheduled data here - only when button is clicked
        }
      }
    } catch (error) {
    }
  };

  useEffect(() => {
    const fetchSupportEmails = async () => {
      const db = getFirestore();
      const configDoc = await getDoc(doc(db, 'DBI Configuration', 'Config'));
      if (configDoc.exists()) {
        setSupportEmails(configDoc.data().Support || []);
      }
    };
    fetchSupportEmails();
  }, []);
  
  useEffect(() => {
    if (result?.savedQueryId || result?.id) {
      const queryId = result.savedQueryId || result.id;
      setSavedId(queryId);
      // Don't fetch scheduled data here - only when button is clicked
    }
  }, [result?.savedQueryId, result?.id]);
  
  // Modified useEffect with check for manual save
  useEffect(() => {
    if (!result || !result.documentId) return;
    
    // If this is a different query than last checked, reset saved state
    if (lastCheckedDocId.current !== result.documentId) {
      manualSaveRef.current = false;
      setIsQuerySaved(false);
      lastCheckedDocId.current = result.documentId;
      
      // Now check with Firestore if this specific query is saved
      checkIfQueryIsSaved(result.documentId);
    }
  }, [result?.documentId]);

  // ADD MOBILE DROPDOWN CLICK OUTSIDE HANDLER
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (mobileDropdownRef.current && !mobileDropdownRef.current.contains(event.target)) {
        setShowMobileDropdown(false);
      }
    };

    if (showMobileDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showMobileDropdown]);

  const fetchScheduledQueryData = async (queryId) => {
    if (!currentUser?.email || !queryId) return;
    
    try {
      setIsLoadingSchedule(true);
      const getScheduledQueries = httpsCallable(functions, 'getScheduledQueries');
      const result = await getScheduledQueries({
        userEmail: currentUser.email
      });

      if (result.data.success) {
        const scheduled = result.data.scheduledQueries.find(
          sq => sq.savedQueryId === queryId
        );
        setScheduledQueryData(scheduled || null);
      }
    } catch (error) {
      console.error('Error fetching scheduled query:', error);
    } finally {
      setIsLoadingSchedule(false);
    }
  };
  
  const copyToClipboard = (text, callback) => {
    navigator.clipboard.writeText(text).then(() => {
      if (typeof callback === 'function') {
        callback();
      } else if (typeof callback === 'object') {
        // For backward compatibility with existing uses
        callback(true);
        setTimeout(() => callback(false), 2000);
      }
    }, (err) => {
    });
  };

  const formatResultForCopy = (data) => {
    if (!Array.isArray(data)) {
      return JSON.stringify(data, null, 2);
    }
    return JSON.stringify(data.map(item => {
      if (typeof item !== 'object' || item === null) {
        return item;
      }
      return Object.fromEntries(
        Object.entries(item).map(([key, value]) => {
          if (value && typeof value === 'object' && 'displayValue' in value) {
            return [key, value.displayValue];
          }
          return [key, value];
        })
      );
    }), null, 2);
  };

  const formatLogs = () => {
    const versions = result.queryVersions || [];
    let logsContent = `Document ID: ${result.documentId}\n`;
    logsContent += `Number of Versions: ${versions.length}\n\n`;
    
    // Add original query information
    logsContent += `User Query:\n${result.userQuery}\n\n`;
    
    // Add configuration details
    logsContent += `Configuration:\n${JSON.stringify(result.config, null, 2)}\n\n`;
    
    // Add versions information
    if (versions.length > 0) {
      logsContent += `Query Versions:\n`;
      versions.forEach((version, index) => {
        logsContent += `\n=== Version ${index + 1} ===\n`;
        if (version.sqlQuery) {
          logsContent += `SQL Query:\n${version.sqlQuery}\n`;
        }
        if (version.bigQueryError) {
          logsContent += `BigQuery Error:\n${version.bigQueryError}\n`;
        }
        logsContent += `\n`;
      });
    }

    // Add final results
    if (result.result && result.result.length > 0) {
      logsContent += `\nFinal Results:\n${formatResultForCopy(result.result)}\n`;
    }

    return logsContent;
  };

  const handleReportIssue = () => {
    const subject = encodeURIComponent(`Support ticket about a query from ${window.location.hostname}, ${format(new Date(), 'dd/MM/yy HH:mm')}`);
    const body = encodeURIComponent(`
Document ID:
${result.documentId}

User Query:
${result.userQuery}
    `);
    const mailtoLink = `mailto:${supportEmails.join(',')}?subject=${subject}&body=${body}`;
    window.open(mailtoLink, '_blank');
  };

  const handleDownload = async () => {
    if (viewMode !== 'table') {
      // Chart download code remains unchanged
      try {
        const wrapper = createImageWrapper(chartRef, cardTitle, statsData, selectedYAxis);
        const canvas = await captureImage(wrapper);
        
        canvas.toBlob((blob) => {
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `${cardTitle || 'chart'}_${format(new Date(), 'yyyyMMdd_HHmmss')}.png`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        }, 'image/png');
      } catch (error) {
        alert('Failed to download chart. Please try again.');
      }
    } else {
      try {
        // Get data to export
        const data = result.processedData || result.result;
        
        // Get visible columns from cardSettings or use all columns as fallback
        const visibleColumns = result.cardSettings?.visibleColumns || [];
        
        // Use the schema to get proper column names, but filter by visible columns
        let columnNames;
        if (visibleColumns.length > 0) {
          // If we have visible columns, use only those
          columnNames = result.schema?.fields
            ?.map(field => field.name)
            .filter(name => visibleColumns.includes(name)) || visibleColumns;
        } else {
          // Fallback to all columns if no visible columns are specified
          columnNames = result.schema?.fields?.map(field => field.name) || Object.keys(data[0]);
        }
        
        // Create a new array with the data in the right format for export
        const exportData = data.map(row => {
          const exportRow = {};
          columnNames.forEach(columnName => {
            // Find the field schema to check if it's a date field
            const fieldSchema = result.schema?.fields?.find(field => field.name === columnName);
            const isDateField = fieldSchema && (
              fieldSchema.type.toUpperCase() === 'DATE' ||
              fieldSchema.type.toUpperCase() === 'DATETIME' ||
              fieldSchema.type.toUpperCase() === 'TIMESTAMP'
            );
            
            // Handle both simple values and objects
            const cellValue = row[columnName];
            if (cellValue && typeof cellValue === 'object') {
              // For date fields, always use displayValue to get the formatted date
              if (isDateField) {
                exportRow[columnName] = cellValue.displayValue ?? cellValue;
              } else {
                // For numeric data, use sortValue (raw number), fall back to displayValue for text
                if (typeof cellValue.sortValue === 'number' && cellValue.sortValue !== Number.NEGATIVE_INFINITY) {
                  exportRow[columnName] = cellValue.sortValue;
                } else {
                  exportRow[columnName] = cellValue.displayValue ?? cellValue.sortValue ?? cellValue;
                }
              }
            } else {
              exportRow[columnName] = cellValue;
            }
          });
          return exportRow;
        });
        
        // Create the worksheet and workbook
        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Data");
        XLSX.writeFile(workbook, `${cardTitle || 'data'}_${format(new Date(), 'yyyyMMdd_HHmmss')}.xlsx`);
      } catch (error) {
        alert('Failed to download table. Please try again.');
      }
    }
  };

  // Check if there are multiple versions to show
  const hasMultipleVersions = () => {
    const versions = result.queryVersions || (result.data && result.data.queryVersions) || [];
    return Array.isArray(versions) && versions.length > 1;
  };

  // Check if download all should be available
  const shouldUseBackgroundDownload = () => {
    // Enable if we have truncation message or more than 2000 rows
    return truncationMessage || (result.totalCount && result.totalCount > 2000);
  };

  const handleShareUrl = async () => {
    setIsGeneratingShareUrl(true);
    
    try {
      let llmQueryId = null;
      
      // For dashboard mode, we need to find the original LLM query ID
      if (isDashboardMode) {
        
        // Check if the query has originalLlmQueryId directly
        if (result.originalLlmQueryId) {
          llmQueryId = result.originalLlmQueryId;
        } 
        // Otherwise, try to get it from Firestore using the query's ID
        else if (result.id || result.savedQueryId) {
          try {
            const queryId = result.savedQueryId || result.id;
            
            const db = getFirestore();
            const savedQueryRef = doc(db, 'SavedQueries', queryId);
            const savedQueryDoc = await getDoc(savedQueryRef);
            
            if (savedQueryDoc.exists()) {
              const savedQueryData = savedQueryDoc.data();
              if (savedQueryData.originalLlmQueryId) {
                llmQueryId = savedQueryData.originalLlmQueryId;
              }
            }
          } catch (error) {
          }
        }
      } 
      // For non-dashboard mode (Ask page)
      else {
        // Direct document ID (typical for Ask page queries)
        llmQueryId = result.documentId;
        
        // If saved query without document ID, look it up
        if (isSavedQuery && !llmQueryId) {
          try {
            const db = getFirestore();
            const savedQueryId = savedId || result.savedQueryId || result.id;
            
            if (savedQueryId) {
              const savedQueryRef = doc(db, 'SavedQueries', savedQueryId);
              const savedQueryDoc = await getDoc(savedQueryRef);
              
              if (savedQueryDoc.exists()) {
                const savedQueryData = savedQueryDoc.data();
                llmQueryId = savedQueryData.originalLlmQueryId;
              }
            }
          } catch (error) {
          }
        }
      }
      
      // Exit if no LLM query ID was found
      if (!llmQueryId) {
        alert('Cannot share this query - missing reference to original query.');
        setIsGeneratingShareUrl(false);
        return;
      }
      
      // Create share URL - always use llmQueryId
      const shareUrl = `${window.location.origin}/ask?llmQueryId=${llmQueryId}`;
    
      try {
        // Update settings in LLM Queries
        const db = getFirestore();
        const docRef = doc(db, 'LLM Queries', llmQueryId);
        const docSnap = await getDoc(docRef);
        
        if (!docSnap.exists()) {
          throw new Error('Query not found');
        }
        
        const queryData = docSnap.data();
        const isOwner = queryData.userId === currentUser?.email;
        
        // Only owner or admin can update settings
        if (isOwner || isAdmin) {
          // Create clean settings object for safe Firestore storage
          const cleanSettings = {
            viewMode: result.cardSettings?.viewMode || 'table',
            chartType: result.cardSettings?.chartType || 'bar',
            selectedXAxis: result.cardSettings?.selectedXAxis || '',
            selectedYAxis: Array.isArray(result.cardSettings?.selectedYAxis) ? 
              result.cardSettings?.selectedYAxis : 
              [result.cardSettings?.selectedYAxis].filter(Boolean),
            visibleColumns: Array.isArray(result.cardSettings?.visibleColumns) ? 
              result.cardSettings?.visibleColumns : [],
            columnOrder: Array.isArray(result.cardSettings?.columnOrder) ? 
              result.cardSettings?.columnOrder : [],
            syncScales: Boolean(result.cardSettings?.syncScales),
            sortColumn: result.cardSettings?.sortColumn || '',
            sortOrder: result.cardSettings?.sortOrder || 'asc',
            showSummary: result.cardSettings?.showSummary ?? true,
            activeFilters: result.cardSettings?.activeFilters || {}
          };
          
          // Add transformation settings if they exist
          if (result.cardSettings?.transformationType) {
            cleanSettings.transformationType = result.cardSettings.transformationType;
          }
          if (result.cardSettings?.pivotConfig) {
            cleanSettings.pivotConfig = result.cardSettings.pivotConfig;
          }
          
          // Update the document with settings AND sharing flags
          await updateDoc(docRef, {
            cardSettings: cleanSettings,
            sharedByLink: true,
            urlShared: true,
            urlSharedAt: new Date(),
            urlSharedBy: currentUser?.email || 'unknown'
          });
        }
        
        // Copy URL to clipboard
        navigator.clipboard.writeText(shareUrl);
        setCopiedShareUrl(true);
        setTimeout(() => setCopiedShareUrl(false), 2000);
        
      } catch (error) {
        alert('Failed to share query');
      }
    } catch (error) {
      alert('An unexpected error occurred while sharing the query');
    } finally {
      setIsGeneratingShareUrl(false);
    }
  };
  

  const handleMakePublicAndShare = async () => {
    try {
      // Use savedId if available, otherwise fall back to result.savedQueryId || result.id
      const queryId = savedId || result.savedQueryId || result.id;
      
      // Update the query to be public
      const db = getFirestore();
      const docRef = doc(db, 'SavedQueries', queryId);
      
      await updateDoc(docRef, {
        isPrivate: false,
        urlShared: true,
        urlSharedAt: new Date(),
        urlSharedBy: currentUser?.email || 'unknown',
        sharedByLink: true  // New field for link-based access
      });
      
      // Create and copy the share URL
      const shareUrl = `${window.location.origin}/ask?queryId=${queryId}`;
      navigator.clipboard.writeText(shareUrl);
      
      setCopiedShareUrl(true);
      setTimeout(() => setCopiedShareUrl(false), 2000);
      
      // Close the warning modal
      setShowSharePrivateWarning(false);
    } catch (error) {
      alert('Failed to update query privacy settings');
    }
  };
  
  const handleAddToLibrary = async () => {
    if (!result || !currentUser || !onAddToLibrary) {
      return;
    }
    
    
    setAddingToLibrary(true);
    try {
      // Pass the result with the title/name explicitly included
      const savedQuery = await onAddToLibrary({
        ...result,
        name: cardTitle || result.name || result.userQuery
      });
      
      // Store the saved ID locally if available
      if (savedQuery && savedQuery.id) {
        setSavedId(savedQuery.id);
      }
      
      // Set the query as saved immediately
      setIsQuerySaved(true);
      // Set the ref to prevent useEffect from overriding
      manualSaveRef.current = true;
    } catch (error) {
      // Still show an error alert as it's important to communicate failures
      alert('Failed to add to library');
    } finally {
      setAddingToLibrary(false);
    }
  };

  // MOBILE DROPDOWN ACTION HANDLER
  const handleMobileActionClick = (action) => {
    setShowMobileDropdown(false);
    action();
  };

  // RENDER MOBILE DROPDOWN VERSION
  if (isMobile) {
    return (
      <div className="relative" ref={mobileDropdownRef}>
        <Button
          onClick={() => setShowMobileDropdown(!showMobileDropdown)}
          icon={MoreHorizontal}
          className="p-2"
          title="Actions"
        />
        
        {showMobileDropdown && (
          <div className="absolute right-0 bottom-full mb-1 bg-white rounded-md shadow-lg py-1 z-50 min-w-40 border border-gray-200">
            {/* Add to Library button */}
            {!isDashboardMode && !isSavedQuery && (
              <button
                onClick={() => handleMobileActionClick(isQuerySaved ? () => {} : handleAddToLibrary)}
                className={`w-full text-left px-4 py-2 text-sm flex items-center ${
                  isQuerySaved 
                    ? 'text-green-500' 
                    : addingToLibrary 
                      ? 'text-gray-400' 
                      : 'hover:bg-gray-100'
                }`}
                disabled={addingToLibrary || isQuerySaved}
              >
                {addingToLibrary ? (
                  <div className="w-4 h-4 border-2 border-t-transparent border-primary rounded-full animate-spin mr-2"></div>
                ) : (
                  <>{isQuerySaved ? <CheckCheck className="mr-2 w-4 h-4" /> : <Bookmark className="mr-2 w-4 h-4" />}</>
                )}
                {isQuerySaved ? "Saved" : addingToLibrary ? "Saving..." : "Save"}
              </button>
            )}

            {/* Rating Button for admin users */}
            {isAdmin && (
              <div className="px-4 py-2">
                <RatingButton 
                  documentId={result.documentId}
                  initialRatings={result.ratings}
                  initialComments={result.ratingComments}
                />
              </div>
            )}

            {/* Download button */}
            {shouldUseBackgroundDownload() ? (
              <div className="px-4 py-2">
                <DownloadAllButton 
                  documentId={result.documentId}
                  userId={currentUser?.email || result.userId || 'anonymous'}
                  fileName={cardTitle || 'query_result'}
                  disabled={!result.documentId}
                  icon={Download}
                  title="Download Data"
                  className="w-full text-left text-sm"
                />
              </div>
            ) : (
              <button
                onClick={() => handleMobileActionClick(handleDownload)}
                className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 flex items-center"
              >
                <Download className="mr-2 w-4 h-4" />
                Download
              </button>
            )}

            {/* Show Logic button for dashboard */}
            {isInDashboard && (
              <button
                onClick={() => handleMobileActionClick(() => {
                  setShowLogicPopup(true);
                  if (!explanation && !isExplanationPending) {
                    onExplanationClick();
                  }
                })}
                className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 flex items-center"
              >
                <Info className="mr-2 w-4 h-4" />
                Show Logic
              </button>
            )}

            {/* Admin buttons */}
            {isAdmin && (
              <>
                <button
                  onClick={() => handleMobileActionClick(() => copyToClipboard(formatResultForCopy(result.result), setCopiedResult))}
                  className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 flex items-center"
                >
                  <Copy className="mr-2 w-4 h-4" />
                  {copiedResult ? 'Copied!' : 'Copy Result'}
                </button>

                <button
                  onClick={() => handleMobileActionClick(() => setShowLogsPopup(true))}
                  className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 flex items-center ${hasMultipleVersions() ? 'text-accent' : ''}`}
                >
                  <FileText className="mr-2 w-4 h-4" />
                  View Logs
                  {hasMultipleVersions() && (
                    <span className="ml-auto h-2 w-2 bg-accent rounded-full"></span>
                  )}
                </button>

                <button
                  onClick={() => handleMobileActionClick(() => setShowSqlPopup(true))}
                  className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 flex items-center"
                >
                  <Code className="mr-2 w-4 h-4" />
                  View SQL
                </button>
              </>
            )}

            {/* Save to Dashboard button */}
            {!isDashboardMode && (
              <button
                onClick={() => handleMobileActionClick(() => {
                  onSaveQuery(cardTitle);
                  setIsQuerySaved(true);
                  manualSaveRef.current = true;
                })}
                className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 flex items-center"
              >
                <FilePlus2 className="mr-2 w-4 h-4" />
                Save to Dashboard
              </button>
            )}

            {/* Schedule button */}
            {(isSavedQuery || isQuerySaved) && (
              <button
                onClick={() => handleMobileActionClick(async () => {
                  const queryId = savedId || result?.savedQueryId || result?.id;
                  if (queryId && !scheduledQueryData && !isLoadingSchedule) {
                    setIsLoadingSchedule(true);
                    try {
                      const getScheduledQueries = httpsCallable(functions, 'getScheduledQueries');
                      const response = await getScheduledQueries({
                        userEmail: currentUser.email
                      });

                      if (response.data.success) {
                        const scheduled = response.data.scheduledQueries.find(
                          sq => sq.savedQueryId === queryId
                        );
                        setScheduledQueryData(scheduled || null);
                        setTimeout(() => setShowScheduleModal(true), 0);
                      } else {
                        setShowScheduleModal(true);
                      }
                    } catch (error) {
                      console.error('Error fetching scheduled query:', error);
                      setShowScheduleModal(true);
                    } finally {
                      setIsLoadingSchedule(false);
                    }
                  } else {
                    setShowScheduleModal(true);
                  }
                })}
                className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 flex items-center"
                disabled={isLoadingSchedule}
              >
                {isLoadingSchedule ? (
                  <div className="w-4 h-4 border-2 border-t-transparent border-primary rounded-full animate-spin mr-2"></div>
                ) : (
                  <CalendarClock className="mr-2 w-4 h-4" />
                )}
                {isLoadingSchedule ? 'Loading...' : 'Schedule'}
              </button>
            )}

            {/* Share URL button */}
            <button
              onClick={() => handleMobileActionClick(handleShareUrl)}
              className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 flex items-center ${copiedShareUrl ? 'text-green-500' : ''}`}
              disabled={isGeneratingShareUrl}
            >
              {isGeneratingShareUrl ? (
                <div className="w-4 h-4 border-2 border-t-transparent border-primary rounded-full animate-spin mr-2"></div>
              ) : (
                <>{copiedShareUrl ? <CheckCheck className="mr-2 w-4 h-4" /> : <Share2 className="mr-2 w-4 h-4" />}</>
              )}
              {copiedShareUrl ? 'Copied URL!' : isGeneratingShareUrl ? 'Generating...' : 'Share URL'}
            </button>

            {/* Report Issue button */}
            <button
              onClick={() => handleMobileActionClick(handleReportIssue)}
              className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 flex items-center"
            >
              <MailQuestion className="mr-2 w-4 h-4" />
              Report Issue
            </button>
          </div>
        )}

        {/* Keep all the modals */}
        <PopupCard
          isOpen={showSqlPopup}
          onClose={() => setShowSqlPopup(false)}
          title="SQL Query"
          footer={
            <Button
              onClick={() => copyToClipboard(result.sqlQuery, setCopiedSql)}
              icon={Copy}
              className="p-2"
              title={copiedSql ? 'Copied!' : 'Copy SQL'}
            />
          }
          className="z-50 font-sans"
          contentClassName="w-full max-w-2xl"
        >
          <div className="max-h-[60vh] overflow-auto">
            <SyntaxHighlighter language="sql" style={docco} customStyle={{ fontSize: '0.875rem' }}>
              {result?.sqlQuery}
            </SyntaxHighlighter>
          </div>
        </PopupCard>

        <LogsModal
          isOpen={showLogsPopup}
          onClose={() => setShowLogsPopup(false)}
          result={result}
          currentUser={currentUser}
          copyToClipboard={copyToClipboard}
        />

        <PopupCard
          isOpen={showLogicPopup}
          onClose={() => setShowLogicPopup(false)}
          title={isEmptyResult ? "Here's How I've Tried Finding the Data" : "Behind the Query"}
          footer={null}
          className="z-50 font-sans"
          contentClassName="w-full max-w-2xl"
        >
          <div className="max-h-[60vh] overflow-auto">
            {isExplanationLoading || isExplanationPending ? (
              <div className="flex justify-center items-center h-full">
                <div className="animate-spin border-4 border-t-4 border-primary rounded-full w-8 h-8"></div>
              </div>
            ) : explanation ? (
              <div className="whitespace-pre-wrap break-words text-sm font-sans">
                {(() => {
                  // Handle both string and object formats
                  if (typeof explanation === 'string') {
                    return explanation;
                  } else if (typeof explanation === 'object' && explanation !== null) {
                    // If it's an object, show the explanation text
                    const explanationText = explanation.explanation || '';
                    const title = explanation.title || '';
                    const filters = Array.isArray(explanation.filters) ? explanation.filters : [];
                    const tables = Array.isArray(explanation.tables) ? explanation.tables : [];
                    
                    // Format the display
                    let displayText = '';
                    if (title) {
                      displayText += `Title: ${title}\n\n`;
                    }
                    if (explanationText) {
                      displayText += `${explanationText}\n\n`;
                    }
                    if (tables.length > 0) {
                      displayText += `Tables Used: ${tables.join(', ')}\n\n`;
                    }
                    if (filters.length > 0) {
                      displayText += `Filters Applied:\n${filters.map(f => `• ${f}`).join('\n')}`;
                    }
                    
                    return displayText || 'No explanation available';
                  }
                  return 'No explanation available';
                })()}
              </div>
            ) : (
              <p className="text-sm text-gray-600 font-sans">
                Explanation not available. Click the "Show Logic" button to generate an explanation.
              </p>
            )}
          </div>
        </PopupCard>

        {console.log('ActionPanel: Modal props', {
          isOpen: showScheduleModal,
          savedQueryId: savedId || result?.savedQueryId || result?.id,
          scheduledQueryId: scheduledQueryData?.id,
          initialData: scheduledQueryData,
          hasScheduledData: !!scheduledQueryData
        })}
        
        <ScheduleQueryModal
          isOpen={showScheduleModal}
          onClose={() => {
            setShowScheduleModal(false);
            // Refresh scheduled data after closing
            if (savedId || result?.savedQueryId || result?.id) {
              fetchScheduledQueryData(savedId || result?.savedQueryId || result?.id);
            }
          }}
          savedQueryId={savedId || result?.savedQueryId || result?.id}
          savedQueryName={cardTitle || result?.name || result?.userQuery}
          scheduledQueryId={scheduledQueryData?.id}
          initialData={scheduledQueryData}
        />

        <PopupCard
          isOpen={showSharePrivateWarning}
          onClose={() => setShowSharePrivateWarning(false)}
          title="Private Query"
          className="z-50 font-sans"
          contentClassName="w-full max-w-md"
        >
          <div className="p-4">
            <p className="mb-4">
              This query is currently private. To share it via URL, you need to make it public first.
            </p>
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => setShowSharePrivateWarning(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={handleMakePublicAndShare}
                className="px-4 py-2 bg-primary text-white rounded hover:bg-primary/90"
              >
                Make Public and Share
              </button>
            </div>
          </div>
        </PopupCard>
      </div>
    );
  }

  // DESKTOP VERSION - ORIGINAL CODE
  return (
    <div className="flex justify-end space-x-2 font-sans">
      {/* Add to Library button */}
      {!isDashboardMode && !isSavedQuery && (
        <Button
          onClick={isQuerySaved ? null : handleAddToLibrary}
          icon={addingToLibrary ? null : (isQuerySaved ? CheckCheck : Bookmark)}
          className={`p-2 md:px-3 relative flex items-center ${
            isQuerySaved 
              ? 'text-green-500 bg-white border border-green-100 rounded-md shadow-glow-sm'
              : addingToLibrary 
                ? 'text-gray-400' 
                : 'text-primary bg-white border border-blue-100 rounded-md shadow-glow-sm'
          }`}
          disabled={addingToLibrary || isQuerySaved}
          title={isQuerySaved ? "Saved to Library" : (addingToLibrary ? "Adding to Library..." : "Add to Library")}
        >
          {addingToLibrary && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-4 h-4 border-2 border-t-transparent border-primary rounded-full animate-spin"></div>
            </div>
          )}
          {!addingToLibrary && <span className="hidden md:inline ml-1">{isQuerySaved ? "Saved" : "Save"}</span>}
        </Button>
      )}
      
      {/* Add the RatingButton for admin users */}
      {isAdmin && (
        <RatingButton 
          documentId={result.documentId}
          initialRatings={result.ratings}
          initialComments={result.ratingComments}
      />
      )}
      
      {/* Temporarily disabled 
      <ShareQueryResult 
        viewMode={viewMode}
        chartRef={chartRef}
        tableData={result.result}
        fileName={`${cardTitle || 'query_result'}_${format(new Date(), 'yyyyMMdd_HHmmss')}`}
        cardTitle={cardTitle}
        statsData={statsData}
        selectedYAxis={selectedYAxis}
      />
      */}
      
      {shouldUseBackgroundDownload() ? (
        <DownloadAllButton 
          documentId={result.documentId}
          userId={currentUser?.email || result.userId || 'anonymous'}
          fileName={cardTitle || 'query_result'}
          disabled={!result.documentId}
          icon={Download}
          title="Download Data"
        />
      ) : (
        <Button
          onClick={handleDownload}
          icon={Download}
          className="p-2"
          title="Download Result"
        />
      )}

      {isInDashboard && (
        <Button
          onClick={() => {
            setShowLogicPopup(true);
            if (!explanation && !isExplanationPending) {
              onExplanationClick();
            }
          }}
          icon={Info}
          className="p-2"
          title="Show Logic"
        />
      )}
  
      {isAdmin && (
        <Button
          onClick={() => copyToClipboard(formatResultForCopy(result.result), setCopiedResult)}
          icon={Copy}
          className="p-2"
          title={copiedResult ? 'Copied!' : 'Copy Result'}
        />
      )}
  
      {isAdmin && (
        <Button
          onClick={() => setShowLogsPopup(true)}
          icon={FileText}
          className={`p-2 relative ${hasMultipleVersions() ? 'text-accent' : ''}`}
          title="View Logs"
        >
          {hasMultipleVersions() && (
            <span className="absolute top-0.5 right-0.5 h-2 w-2 bg-accent rounded-full"></span>
          )}
        </Button>
      )}
  
      {isAdmin && (
        <Button
          onClick={() => setShowSqlPopup(true)}
          icon={Code}
          className="p-2"
          title="View SQL"
        />
      )}
  
  {!isDashboardMode && (
  <Button
    onClick={() => {
      // Pass the cardTitle to onSaveQuery
      onSaveQuery(cardTitle);
      // Immediately set as saved so UI updates
      setIsQuerySaved(true);
      // Set the ref to prevent useEffect from overriding
      manualSaveRef.current = true;
    }}
    icon={FilePlus2}
    className="p-2"
    title="Save to Dashboard"
  />
)}

      {/* Schedule button - Updated condition */}
      {(isSavedQuery || isQuerySaved) && (
        <Button
          onClick={async () => {
            const queryId = savedId || result?.savedQueryId || result?.id;
            
            if (queryId && !scheduledQueryData && !isLoadingSchedule) {
              setIsLoadingSchedule(true);
              try {
                const getScheduledQueries = httpsCallable(functions, 'getScheduledQueries');
                const response = await getScheduledQueries({
                  userEmail: currentUser.email
                });

                if (response.data.success) {
                  const scheduled = response.data.scheduledQueries.find(
                    sq => sq.savedQueryId === queryId
                  );
                  setScheduledQueryData(scheduled || null);
                  setTimeout(() => setShowScheduleModal(true), 0);
                } else {
                  setShowScheduleModal(true);
                }
              } catch (error) {
                console.error('Error fetching scheduled query:', error);
                setShowScheduleModal(true);
              } finally {
                setIsLoadingSchedule(false);
              }
            } else {
              setShowScheduleModal(true);
            }
          }}
          icon={isLoadingSchedule ? null : CalendarClock}
          className="p-2"
          title="Schedule"
          disabled={isLoadingSchedule}
        >
          {isLoadingSchedule && (
            <div className="w-4 h-4 border-2 border-t-transparent border-primary rounded-full animate-spin"></div>
          )}
        </Button>
      )}

      {/* Share URL button - hidden in dashboard mode */}
        <div className="relative">
          <Button
            onClick={handleShareUrl}
            icon={copiedShareUrl ? CheckCheck : (isGeneratingShareUrl ? null : Share2)}
            className={`p-2 ${copiedShareUrl ? 'text-green-500' : ''} relative`}
            title="Share URL"
            disabled={isGeneratingShareUrl}
          >
            {/* Loading spinner while generating URL */}
            {isGeneratingShareUrl && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-4 h-4 border-2 border-t-transparent border-primary rounded-full animate-spin"></div>
              </div>
            )}
          </Button>
          
          {/* Tooltip that appears when URL is copied */}
          {copiedShareUrl && (
            <div className="absolute top-1/2 right-full mr-2 -translate-y-1/2 px-3 py-1 bg-green-50 text-green-700 text-xs rounded shadow-md border border-green-200 whitespace-nowrap z-50 animate-fadeIn">
              Copied URL!
              <div className="absolute top-1/2 left-full -translate-y-1/2 w-2 h-2 bg-green-50 rotate-45 border-l border-t border-green-200"></div>
            </div>
          )}
        </div>

      <Button
        onClick={handleReportIssue}
        icon={MailQuestion}
        className="p-2"
        title="Report Issue"
      />
  
      <PopupCard
        isOpen={showSqlPopup}
        onClose={() => setShowSqlPopup(false)}
        title="SQL Query"
        footer={
          <Button
            onClick={() => copyToClipboard(result.sqlQuery, setCopiedSql)}
            icon={Copy}
            className="p-2"
            title={copiedSql ? 'Copied!' : 'Copy SQL'}
          />
        }
        className="z-50 font-sans"
        contentClassName="w-full max-w-2xl"
      >
        <div className="max-h-[60vh] overflow-auto">
          <SyntaxHighlighter language="sql" style={docco} customStyle={{ fontSize: '0.875rem' }}>
            {result?.sqlQuery}
          </SyntaxHighlighter>
        </div>
      </PopupCard>
  
      <LogsModal
        isOpen={showLogsPopup}
        onClose={() => setShowLogsPopup(false)}
        result={result}
        currentUser={currentUser}
        copyToClipboard={copyToClipboard}
      />
  
      <PopupCard
        isOpen={showLogicPopup}
        onClose={() => setShowLogicPopup(false)}
        title={isEmptyResult ? "Here's How I've Tried Finding the Data" : "Behind the Query"}
        footer={null}
        className="z-50 font-sans"
        contentClassName="w-full max-w-2xl"
      >
        <div className="max-h-[60vh] overflow-auto">
          {isExplanationLoading || isExplanationPending ? (
            <div className="flex justify-center items-center h-full">
              <div className="animate-spin border-4 border-t-4 border-primary rounded-full w-8 h-8"></div>
            </div>
          ) : explanation ? (
            <div className="whitespace-pre-wrap break-words text-sm font-sans">
              {(() => {
                // Handle both string and object formats
                if (typeof explanation === 'string') {
                  return explanation;
                } else if (typeof explanation === 'object' && explanation !== null) {
                  // If it's an object, show the explanation text
                  const explanationText = explanation.explanation || '';
                  const title = explanation.title || '';
                  const filters = Array.isArray(explanation.filters) ? explanation.filters : [];
                  const tables = Array.isArray(explanation.tables) ? explanation.tables : [];
                  
                  // Format the display
                  let displayText = '';
                  if (title) {
                    displayText += `Title: ${title}\n\n`;
                  }
                  if (explanationText) {
                    displayText += `${explanationText}\n\n`;
                  }
                  if (tables.length > 0) {
                    displayText += `Tables Used: ${tables.join(', ')}\n\n`;
                  }
                  if (filters.length > 0) {
                    displayText += `Filters Applied:\n${filters.map(f => `• ${f}`).join('\n')}`;
                  }
                  
                  return displayText || 'No explanation available';
                }
                return 'No explanation available';
              })()}
            </div>
          ) : (
            <p className="text-sm text-gray-600 font-sans">
              Explanation not available. Click the "Show Logic" button to generate an explanation.
            </p>
          )}
        </div>
      </PopupCard>

      <ScheduleQueryModal
        isOpen={showScheduleModal}
        onClose={() => {
          setShowScheduleModal(false);
          // Refresh scheduled data after closing
          if (savedId || result?.savedQueryId || result?.id) {
            fetchScheduledQueryData(savedId || result?.savedQueryId || result?.id);
          }
        }}
        savedQueryId={savedId || result?.savedQueryId || result?.id}
        savedQueryName={cardTitle || result?.name || result?.userQuery}
        scheduledQueryId={scheduledQueryData?.id}
        initialData={scheduledQueryData}
      />

      <PopupCard
        isOpen={showSharePrivateWarning}
        onClose={() => setShowSharePrivateWarning(false)}
        title="Private Query"
        className="z-50 font-sans"
        contentClassName="w-full max-w-md"
      >
        <div className="p-4">
          <p className="mb-4">
            This query is currently private. To share it via URL, you need to make it public first.
          </p>
          <div className="flex justify-end space-x-2">
            <button
              onClick={() => setShowSharePrivateWarning(false)}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
            >
              Cancel
            </button>
            <button
              onClick={handleMakePublicAndShare}
              className="px-4 py-2 bg-primary text-white rounded hover:bg-primary/90"
            >
              Make Public and Share
            </button>
          </div>
        </div>
      </PopupCard>
    </div>
  );
};

export default ActionPanel;